/**
 * WeaveIn.ai Platform - Duplicate Match Panel
 * Displays detected duplicate matches with scoring, resolution, and merge actions
 */

import React, { useState } from 'react';
import {
  AlertTriangle, CheckCircle, XCircle, GitMerge, User, Phone, Mail, Calendar,
  ChevronDown, ChevronUp, Loader2
} from 'lucide-react';
import { DuplicateMatch } from '@/services/api/contacts';

interface DuplicateMatchPanelProps {
  contactId: number;
  matches: DuplicateMatch[];
  onResolve: (matchId: number, resolution: string, notes?: string) => Promise<boolean>;
  onMerge: (targetId: number) => Promise<boolean>;
  onRefresh?: () => void;
}

function ScoreBar({ score, max = 100 }: { score: number; max?: number }) {
  const pct = Math.min(100, (score / max) * 100);
  const color = pct >= 70 ? 'bg-red-500' : pct >= 40 ? 'bg-orange-500' : 'bg-yellow-500';
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{score.toFixed(0)}%</span>
    </div>
  );
}

function FieldMatchIndicator({ label, matched, score }: { label: string; matched: boolean; score: number }) {
  return (
    <div className="flex items-center gap-1.5 text-xs">
      {matched ? (
        <CheckCircle className="w-3 h-3 text-green-500" />
      ) : (
        <XCircle className="w-3 h-3 text-gray-300" />
      )}
      <span className={matched ? 'text-green-700 dark:text-green-400 font-medium' : 'text-gray-400'}>
        {label}
      </span>
      {matched && <span className="text-gray-400">({score.toFixed(0)})</span>}
    </div>
  );
}

export default function DuplicateMatchPanel({ contactId, matches, onResolve, onMerge, onRefresh }: DuplicateMatchPanelProps) {
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [mergeConfirm, setMergeConfirm] = useState<number | null>(null);
  const [processing, setProcessing] = useState<number | null>(null);
  const [resolveNotes, setResolveNotes] = useState('');

  const pendingMatches = matches.filter((m) => m.resolution === 'pending');
  const resolvedMatches = matches.filter((m) => m.resolution !== 'pending');

  const handleResolve = async (matchId: number, resolution: string) => {
    setProcessing(matchId);
    try {
      await onResolve(matchId, resolution, resolveNotes || undefined);
      setResolveNotes('');
      onRefresh?.();
    } finally {
      setProcessing(null);
    }
  };

  const handleMerge = async (targetId: number) => {
    setProcessing(targetId);
    try {
      await onMerge(targetId);
      setMergeConfirm(null);
      onRefresh?.();
    } finally {
      setProcessing(null);
    }
  };

  if (matches.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-gray-400">
        <CheckCircle className="w-6 h-6 mx-auto mb-2 text-green-400" />
        No duplicate matches detected
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Pending */}
      {pendingMatches.length > 0 && (
        <div>
          <h4 className="flex items-center gap-2 text-sm font-medium text-orange-700 dark:text-orange-300 mb-2">
            <AlertTriangle className="w-4 h-4" />
            {pendingMatches.length} Pending Review
          </h4>
          <div className="space-y-2">
            {pendingMatches.map((match) => {
              const isExpanded = expandedId === match.id;
              const fields = match.match_fields as any;

              return (
                <div key={match.id} className="border border-orange-200 dark:border-orange-800 rounded-lg overflow-hidden">
                  {/* Summary row */}
                  <div
                    className="flex items-center justify-between px-4 py-3 bg-orange-50 dark:bg-orange-900/20 cursor-pointer"
                    onClick={() => setExpandedId(isExpanded ? null : match.id)}
                  >
                    <div className="flex items-center gap-3">
                      <User className="w-4 h-4 text-gray-400" />
                      <div>
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {match.matched_first_name} {match.matched_last_name}
                        </span>
                        <span className="ml-2 text-xs text-gray-400">{match.matched_member_number}</span>
                        <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                          match.matched_record_type === 'member' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {match.matched_record_type}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <ScoreBar score={match.match_score} />
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </div>
                  </div>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="px-4 py-3 bg-white dark:bg-gray-800 space-y-3">
                      {/* Matched PII */}
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        {match.matched_phone_masked && (
                          <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300">
                            <Phone className="w-3.5 h-3.5" /> {match.matched_phone_masked}
                          </div>
                        )}
                        {match.matched_email_masked && (
                          <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300">
                            <Mail className="w-3.5 h-3.5" /> {match.matched_email_masked}
                          </div>
                        )}
                        {match.matched_dob && (
                          <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300">
                            <Calendar className="w-3.5 h-3.5" /> {match.matched_dob}
                          </div>
                        )}
                      </div>

                      {/* Field match breakdown */}
                      {fields && (
                        <div className="flex flex-wrap gap-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                          {fields.phone && <FieldMatchIndicator label="Phone" matched={fields.phone.matched} score={fields.phone.score} />}
                          {fields.email && <FieldMatchIndicator label="Email" matched={fields.email.matched} score={fields.email.score} />}
                          {fields.name && <FieldMatchIndicator label="Name" matched={fields.name.matched} score={fields.name.score} />}
                          {fields.dob && <FieldMatchIndicator label="DOB" matched={fields.dob.matched} score={fields.dob.score} />}
                          {fields.zip && <FieldMatchIndicator label="Zip" matched={fields.zip.matched} score={fields.zip.score} />}
                        </div>
                      )}

                      {/* Notes */}
                      <div>
                        <input
                          type="text"
                          placeholder="Optional notes..."
                          value={resolveNotes}
                          onChange={(e) => setResolveNotes(e.target.value)}
                          className="w-full px-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-900 dark:text-gray-100"
                        />
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={() => handleResolve(match.id, 'confirmed_different')}
                          disabled={processing === match.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          <XCircle className="w-3.5 h-3.5" /> Different Person
                        </button>
                        <button
                          onClick={() => setMergeConfirm(match.matched_member_id)}
                          disabled={processing === match.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700"
                        >
                          {processing === match.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <GitMerge className="w-3.5 h-3.5" />
                          )}
                          Same Person &mdash; Merge
                        </button>
                      </div>

                      {/* Merge confirmation */}
                      {mergeConfirm === match.matched_member_id && (
                        <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-sm">
                          <p className="font-medium text-red-700 dark:text-red-300 mb-2">Confirm Merge</p>
                          <p className="text-red-600 dark:text-red-400 text-xs mb-3">
                            This will merge this contact into the existing record ({match.matched_first_name} {match.matched_last_name}).
                            Missing fields will be filled from this contact. This contact will be deleted.
                          </p>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setMergeConfirm(null)}
                              className="px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleMerge(match.matched_member_id)}
                              disabled={processing === match.matched_member_id}
                              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700"
                            >
                              {processing === match.matched_member_id && <Loader2 className="w-3 h-3 animate-spin" />}
                              Confirm Merge
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Resolved */}
      {resolvedMatches.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-500 mb-2">Resolved ({resolvedMatches.length})</h4>
          <div className="space-y-1">
            {resolvedMatches.map((match) => (
              <div key={match.id} className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded text-sm">
                <span className="text-gray-600 dark:text-gray-300">
                  {match.matched_first_name} {match.matched_last_name}
                  <span className="ml-2 text-gray-400 text-xs">{match.matched_member_number}</span>
                </span>
                <span className={`text-xs px-2 py-0.5 rounded ${
                  match.resolution === 'merged' ? 'bg-purple-100 text-purple-700' :
                  match.resolution === 'confirmed_same' ? 'bg-green-100 text-green-700' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {match.resolution?.replace('_', ' ')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
